import React from "react";
import { createTheme } from "@mui/material/styles";
import { Ring } from "../components/loading";

/**
 * MUI 主題 —— Material Design 3 的外觀，數值對齊 styles/tokens.css。
 *
 * 顏色：MUI 要拿實際色碼去算對比與半透明，不能直接塞 var(--primary)，
 * 所以這裡把 tokens.css 的淺色／深色兩組值抄一份。改顏色時兩邊要一起改。
 *
 * 字級：一律寫成 var(--t-*)。<html data-textsize="lg"> 會把整組變數換成大字，
 * MUI 元件就跟著放大，不需要另外產一個大字主題。
 *
 * 命中區：按鈕、輸入框的高度走 --t-control-h（≥ 44px），規則 3 照舊成立。
 */
const PALETTE = {
  light: {
    primary: { main: "#1d3b6f", dark: "#152c54", light: "#e6ecf6", contrastText: "#ffffff" },
    error: { main: "#b3261e", dark: "#8f1f19", light: "#fdeceb", contrastText: "#ffffff" },
    success: { main: "#1a6b3f", dark: "#14552f", light: "#e6f3ea", contrastText: "#ffffff" },
    warning: { main: "#8a5300", dark: "#6b4000", light: "#fdf3e2", contrastText: "#ffffff" },
    info: { main: "#1d3b6f", dark: "#152c54", light: "#e6ecf6", contrastText: "#ffffff" },
    grey: { 500: "#5c6573", 100: "#eceef2", 700: "#414957" },
    background: { default: "#f4f6f9", paper: "#ffffff" },
    text: { primary: "#1a2233", secondary: "#525d70", disabled: "#8590a2" },
    divider: "#dde2e9",
    action: { hover: "#eef1f6", selected: "#e6ecf6" },
  },
  dark: {
    primary: { main: "#d6d6d6", dark: "#f0f0f0", light: "#333333", contrastText: "#141414" },
    error: { main: "#ff9b8f", dark: "#ff9b8f", light: "#3a201c", contrastText: "#2a0d09" },
    success: { main: "#6fd39a", dark: "#6fd39a", light: "#16301f", contrastText: "#0b2215" },
    warning: { main: "#f5c98a", dark: "#f5c98a", light: "#352713", contrastText: "#2a1c05" },
    info: { main: "#d6d6d6", dark: "#f0f0f0", light: "#333333", contrastText: "#141414" },
    grey: { 500: "#a6a6a6", 100: "#2a2a2a", 700: "#c8c8c8" },
    background: { default: "#121212", paper: "#1e1e1e" },
    text: { primary: "#ececec", secondary: "#b3b3b3", disabled: "#8f8f8f" },
    divider: "#424242",
    action: { hover: "#2a2a2a", selected: "#333333" },
  },
};

const t = (name) => ({ fontSize: `var(--t-${name})`, lineHeight: `var(--t-${name}-lh)` });

export function createAppTheme(mode = "light") {
  return createTheme({
    palette: { mode, ...PALETTE[mode] },
    /* 圓角刻度與 tokens.css 的 --radius-* 同一套：xs 4、sm 8、md 12、pill 999。
       shape.borderRadius 是 MUI 元件的預設值（以及 sx 裡 borderRadius: 1 的單位），取 sm。 */
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: "var(--font-sans)",
      h1: { ...t("display"), fontWeight: 700 },
      h2: { ...t("title"), fontWeight: 700 },
      h3: { ...t("section"), fontWeight: 700 },
      h4: { ...t("section"), fontWeight: 700 },
      h5: { ...t("body"), fontWeight: 700 },
      h6: { ...t("body"), fontWeight: 600 },
      subtitle1: { ...t("body"), fontWeight: 600 },
      subtitle2: { ...t("meta"), fontWeight: 600 },
      body1: t("body"),
      body2: t("meta"),
      caption: t("meta"),
      overline: t("meta"),
      button: { ...t("button"), fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiButtonBase: { defaultProps: { disableRipple: false } },
      MuiButton: {
        /* loading 時用 loading-ui 的轉圈（components/loading），全站按鈕同一個樣子 */
        defaultProps: { disableElevation: true, variant: "outlined", loadingIndicator: <Ring size={1.1} label="處理中" /> },
        styleOverrides: {
          root: {
            minHeight: "var(--t-control-h)",
            minWidth: "var(--hit-min)",
            paddingInline: 24,
            /* 按鈕與輸入框同一個圓角：表單裡並排時才不會一個方一個圓 */
            borderRadius: "var(--radius-sm)",
            whiteSpace: "nowrap",
          },
          outlined: { borderWidth: 1 },
          text: { paddingInline: 12 },
          sizeSmall: { minHeight: "var(--hit-min)", paddingInline: 16 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { width: "var(--t-control-h)", height: "var(--t-control-h)", fontSize: "var(--icon)" },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: false, enterDelay: 200 },
        styleOverrides: { tooltip: { fontSize: "var(--t-meta)", lineHeight: "var(--t-meta-lh)", borderRadius: "var(--radius-xs)" } },
      },
      /* 浮層加一圈細框：深色模式下陰影幾乎看不見，沒有框就分不出面板的邊界 */
      MuiMenu: {
        styleOverrides: { paper: { borderRadius: "var(--radius-md)", minWidth: 200, border: "1px solid var(--line-overlay)" } },
      },
      MuiPopover: { styleOverrides: { paper: { borderRadius: "var(--radius-md)", border: "1px solid var(--line-overlay)" } } },
      MuiMenuItem: { styleOverrides: { root: { minHeight: "var(--hit-min)", gap: 12, ...t("body") } } },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 0, fontSize: "var(--icon)", color: "inherit" } } },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: { root: { borderRadius: "var(--radius-md)" } },
      },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: "var(--radius-md)" } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: "var(--radius-sm)", height: "auto", minHeight: 32, ...t("meta"), fontWeight: 600 },
          label: { paddingBlock: 4, whiteSpace: "nowrap" },
          icon: { fontSize: "1em", marginLeft: 10 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: "var(--radius-md)", ...t("body"), alignItems: "flex-start" },
          icon: { fontSize: "var(--icon)", paddingBlock: 2 },
          message: { paddingBlock: 2 },
        },
      },
      MuiInputBase: { styleOverrides: { root: { ...t("body") } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: "var(--radius-sm)", minHeight: "var(--t-control-h)" },
          notchedOutline: { borderColor: "var(--line-control)" },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { ...t("body") } } },
      MuiFormHelperText: { styleOverrides: { root: { ...t("meta"), marginInline: 0 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: "var(--radius-md)" } } },
      MuiDialogTitle: { styleOverrides: { root: { ...t("title"), fontWeight: 700 } } },
      MuiTab: {
        styleOverrides: { root: { ...t("body"), fontWeight: 600, textTransform: "none", minHeight: "var(--hit-min)" } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { height: 8, borderRadius: "var(--radius-pill)" }, bar: { borderRadius: "var(--radius-pill)" } },
      },
      MuiListItemButton: { styleOverrides: { root: { minHeight: 56 } } },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: "1px solid var(--line-hairline)",
            borderRadius: "var(--radius-md)",
            "&:before": { display: "none" },
            /* 疊在一起的收合區，MUI 會把中間幾個的圓角拿掉；這裡讓每個都保留同一個圓角 */
            "&:first-of-type, &:last-of-type": { borderRadius: "var(--radius-md)" },
          },
        },
      },
      MuiAccordionSummary: { styleOverrides: { root: { minHeight: 56, ...t("section"), fontWeight: 700 } } },
      MuiSnackbarContent: { styleOverrides: { root: { ...t("body"), borderRadius: "var(--radius-md)" } } },
      MuiAvatar: { styleOverrides: { rounded: { borderRadius: "var(--radius-sm)" } } },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: "var(--radius-md)" } } },
      /* 切換鈕的框線跟浮層同一階（深色模式下一般分隔線太暗，看不出按鈕的邊界） */
      MuiToggleButton: { styleOverrides: { root: { borderColor: "var(--line-overlay)" } } },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { borderRadius: "var(--radius-sm)" },
          grouped: {
            borderRadius: 0,
            "&:first-of-type": { borderTopLeftRadius: "var(--radius-sm)", borderBottomLeftRadius: "var(--radius-sm)" },
            "&:last-of-type": { borderTopRightRadius: "var(--radius-sm)", borderBottomRightRadius: "var(--radius-sm)" },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { ...t("body"), borderColor: "var(--line-hairline)" },
          head: { ...t("meta"), fontWeight: 700, color: "var(--ink-muted)", whiteSpace: "nowrap" },
        },
      },
      MuiBadge: { styleOverrides: { badge: { fontWeight: 700 } } },
    },
  });
}
